import { useMemo } from "react";
import { Text, useMantineTheme, Flex } from "@mantine/core";
import { MantineReactTable, type MRT_ColumnDef } from "mantine-react-table";
import { TransactionTypeBadge, SpendingTypeBadge } from "@/pages/admin/AdminStatusBadge";
import { formatINR, formatDate } from "@/shared/utils/format.utils";

type ExpenseRow = {
  id: string;
  date: string;
  transactionType: string;
  description: string;
  amount: number | string;
  categoryName: string | null;
  needWant: string | null;
};

type Props = {
  expenseEntries: any[];
  expenseCategories: any[];
};

export default function ExpenseLogsTab({ expenseEntries, expenseCategories }: Props) {
  const theme = useMantineTheme();

  // Build categoryId → category for cross-referencing
  const categoryById = useMemo(() => {
    const map: Record<string, any> = {};
    for (const c of expenseCategories) map[c.id] = c;
    return map;
  }, [expenseCategories]);

  const rows: ExpenseRow[] = useMemo(
    () =>
      expenseEntries.map((e) => {
        const category = e.categoryId ? categoryById[e.categoryId] : null;
        return {
          id: e.id,
          date: e.entryDate,
          transactionType: e.type,
          description: e.description,
          amount: e.amount,
          categoryName: category?.name ?? null,
          needWant: e.needWant ?? null,
        };
      }),
    [expenseEntries, categoryById]
  );

  const columns: MRT_ColumnDef<ExpenseRow>[] = useMemo(
    () => [
      {
        accessorKey: "date",
        header: "Date",
        size: 160,
        enableColumnFilter: false,
        Cell: ({ cell }) => formatDate(cell.getValue<string>()),
      },
      {
        accessorKey: "transactionType",
        header: "Transaction Type",
        size: 160,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => <TransactionTypeBadge type={cell.getValue<string>()} />,
      },
      {
        accessorKey: "description",
        header: "Description",
        size: 220,
      },
      {
        accessorKey: "amount",
        header: "Amount",
        size: 130,
        enableColumnFilter: false,
        Cell: ({ cell }) => formatINR(cell.getValue<number | string>()),
      },
      {
        accessorKey: "categoryName",
        header: "Category",
        size: 150,
        Cell: ({ cell }) => {
          const name = cell.getValue<string | null>();
          if (!name) return <span style={{ color: "var(--mantine-color-slate-4)" }}>—</span>;
          return <Text fz="sm">{name}</Text>;
        },
      },
      {
        accessorKey: "needWant",
        header: "Spending Type",
        size: 140,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => <SpendingTypeBadge type={cell.getValue<string | null>()} />,
      },
    ],
    []
  );

  return (
    <Flex direction="column" gap="md">
      <Text fw={500} fz="md">
        All transactions
      </Text>

      <MantineReactTable
        columns={columns}
        data={rows}
        enableRowNumbers
        enableStickyHeader
        enableColumnPinning
        enableColumnResizing
        columnResizeMode="onEnd"
        layoutMode="semantic"
        enableGlobalFilter={false}
        positionToolbarAlertBanner="none"
        initialState={{
          density: "xs",
        }}
        mantineTableBodyCellProps={{
          style: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            borderRight: "1px solid var(--mantine-color-gray-2)",
          },
        }}
        mantineTableHeadCellProps={{
          style: {
            fontWeight: 400,
            fontSize: "var(--mantine-font-size-sm)",
            color: theme.colors.slate[5],
            borderRight: "1px solid var(--mantine-color-gray-2)",
            backgroundColor: "var(--mantine-color-gray-0)",
          },
        }}
        mantineTableContainerProps={{
          style: {
            maxHeight: "calc(100vh - 300px)",
            minHeight: "100px",
            width: "100%",
            overflowX: "auto",
          },
        }}
      />
    </Flex>
  );
}
